import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, UploadCloud, FileMusic, FileText, Layers, Package, Paperclip, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";

// ---------------------------------------------------------------------------
// MP8 corrective addition — internal-only demo intake dialog.
//
// Mounted by Demos.jsx behind a `+ New Internal Demo Intake` button.
// RBAC is enforced server-side; the parent component already gates visibility
// to super_admin / catalog_manager / gfmp_reviewer.
//
// Posts multipart to POST /api/demos/internal-intake — the same backend
// pipeline used for public submissions (AI screening + audit + workflow
// routing), with these additions:
//
//   * visibility: "internal_only" (server-set)
//   * intake_source: "internal_admin" (server-set)
//   * priority, initial workflow, reviewer assignment, intended destination,
//     source/provenance notes, internal notes — captured at create time.
//
// On success: navigates straight to /demos/{new_demo_id} so the reviewer
// continues in the standard detail view.
// ---------------------------------------------------------------------------

const FILE_SLOTS = [
  { slot: "audio",            label: "Audio (required)",        kind: "audio",      Icon: FileMusic,  required: true  },
  { slot: "lyrics",           label: "Lyrics",                  kind: "lyrics",     Icon: FileText,   required: false },
  { slot: "stems",            label: "Stems archive",           kind: "stems",      Icon: Layers,     required: false },
  { slot: "daw_project",      label: "DAW project / session",   kind: "daw",        Icon: Package,    required: false },
  { slot: "supporting_doc_1", label: "Supporting doc (slot 1)", kind: "supporting", Icon: Paperclip,  required: false },
  { slot: "supporting_doc_2", label: "Supporting doc (slot 2)", kind: "supporting", Icon: Paperclip,  required: false },
  { slot: "supporting_doc_3", label: "Supporting doc (slot 3)", kind: "supporting", Icon: Paperclip,  required: false },
];

const FILE_ACCEPT_BY_KIND = {
  audio:      "audio/*,.wav,.mp3,.m4a,.aiff,.aif,.flac,.aac,.ogg",
  lyrics:     ".txt,.pdf,.docx,.rtf",
  stems:      ".zip,.rar,.7z",
  daw:        ".zip,.rar,.7z,.als,.flp,.logicx,.cpr,.ptx",
  supporting: ".pdf,.docx,.txt,.png,.jpg,.jpeg,.zip",
};

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes >= 1024 ** 3) return (bytes / (1024 ** 3)).toFixed(2) + " GB";
  if (bytes >= 1024 ** 2) return (bytes / (1024 ** 2)).toFixed(1) + " MB";
  if (bytes >= 1024)      return (bytes / 1024).toFixed(1) + " KB";
  return `${bytes} B`;
}

const BLANK_FORM = {
  submitter_full_legal_name: "",
  artist_producer_name: "",
  email: "",
  phone: "",
  song_title: "",
  alternate_title: "",
  primary_genre: "",
  approximate_completion_date: "",
  development_notes: "",
  known_contributors: "",
  known_third_party_elements: "",
  source_provenance: "",
  internal_notes: "",
  priority: "Normal",
  assigned_reviewer_email: "",
  intended_destination: "",
  initial_workflow_status: "",
};

const BLANK_FILES = {
  audio: null,
  lyrics: null,
  stems: null,
  daw_project: null,
  supporting_doc_1: null,
  supporting_doc_2: null,
  supporting_doc_3: null,
};

export function InternalDemoIntakeDialog({ open, onOpenChange, meta, onCreated }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(BLANK_FORM);
  const [files, setFiles] = useState(BLANK_FILES);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const fileInputRefs = useRef({});

  useEffect(() => {
    if (!open) {
      setForm(BLANK_FORM);
      setFiles(BLANK_FILES);
      setSubmitError("");
      setSubmitting(false);
    }
  }, [open]);

  function setField(k, v) { setForm((s) => ({ ...s, [k]: v })); }
  function setFileSlot(k, v) { setFiles((s) => ({ ...s, [k]: v })); }

  const totalBytes = useMemo(
    () => Object.values(files).reduce((a, f) => a + (f?.size || 0), 0),
    [files]
  );

  async function submit() {
    setSubmitError("");
    if (!form.song_title.trim()) {
      setSubmitError("Song title is required.");
      return;
    }
    if (!files.audio) {
      setSubmitError("Audio file is required for screening.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      Object.entries(files).forEach(([slot, file]) => {
        if (file) fd.append(slot, file);
      });
      const r = await api.post("/demos/internal-intake", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      const newId = r.data?.demo_id;
      toast.success(`Internal demo created (${r.data?.ref_id || "intake"}).`);
      if (typeof onCreated === "function") onCreated(r.data);
      onOpenChange(false);
      if (newId) navigate(`/demos/${newId}`);
    } catch (e) {
      setSubmitError(errMsg(e, "Could not create internal demo."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="internal-intake-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UploadCloud className="w-4 h-4" />
            New internal demo intake
          </DialogTitle>
          <DialogDescription>
            Staff-only doorway. The record is created with
            <span className="font-mono mx-1 px-1.5 py-0.5 rounded bg-muted text-[11px]">visibility: internal_only</span>
            and routes through the same AI screening, documentation, contract, hold,
            and promote-to-track lifecycle as a public submission.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2" data-testid="internal-intake-body">
          {/* Identification */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
              1 — Identification
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Submitter / rights-holder name</Label>
                <Input
                  value={form.submitter_full_legal_name}
                  onChange={(e) => setField("submitter_full_legal_name", e.target.value)}
                  placeholder="Full legal name"
                  data-testid="intake-submitter-name"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Artist / producer name</Label>
                <Input
                  value={form.artist_producer_name}
                  onChange={(e) => setField("artist_producer_name", e.target.value)}
                  data-testid="intake-artist-name"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Contact email (optional)</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                  data-testid="intake-email"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Contact phone (optional)</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value)}
                  data-testid="intake-phone"
                />
              </div>
            </div>
          </section>

          {/* Recording */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
              2 — Recording
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wide">
                  Song title <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={form.song_title}
                  onChange={(e) => setField("song_title", e.target.value)}
                  data-testid="intake-song-title"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Alternate title</Label>
                <Input
                  value={form.alternate_title}
                  onChange={(e) => setField("alternate_title", e.target.value)}
                  data-testid="intake-alt-title"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Primary genre</Label>
                <Input
                  value={form.primary_genre}
                  onChange={(e) => setField("primary_genre", e.target.value)}
                  data-testid="intake-genre"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Approximate completion</Label>
                <Input
                  value={form.approximate_completion_date}
                  onChange={(e) => setField("approximate_completion_date", e.target.value)}
                  placeholder="e.g. Q4 2025"
                  data-testid="intake-completion"
                />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-[11px] uppercase tracking-wide">Development notes</Label>
              <Textarea
                rows={3}
                value={form.development_notes}
                onChange={(e) => setField("development_notes", e.target.value)}
                data-testid="intake-dev-notes"
              />
            </div>
          </section>

          {/* Contributors & provenance */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
              3 — Contributors &amp; provenance
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wide">
                  Known writers / producers / contributors
                </Label>
                <Textarea
                  rows={3}
                  value={form.known_contributors}
                  onChange={(e) => setField("known_contributors", e.target.value)}
                  placeholder="One per line is fine. Roles + known splits if available."
                  data-testid="intake-contributors"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">
                  Known third-party elements (samples, interpolations, AI assistance, library cues)
                </Label>
                <Textarea
                  rows={3}
                  value={form.known_third_party_elements}
                  onChange={(e) => setField("known_third_party_elements", e.target.value)}
                  data-testid="intake-third-party"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">
                  Source / provenance notes (internal only)
                </Label>
                <Textarea
                  rows={3}
                  value={form.source_provenance}
                  onChange={(e) => setField("source_provenance", e.target.value)}
                  placeholder="Where this came from — e.g. legacy session, client deposit, Scott / Frank archive, etc."
                  data-testid="intake-provenance"
                />
              </div>
            </div>
          </section>

          {/* Files */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
              4 — Files (audio required)
            </div>
            <div className="space-y-2">
              {FILE_SLOTS.map(({ slot, label, kind, Icon, required }) => {
                const file = files[slot];
                const inputId = `intake-file-${slot}`;
                return (
                  <div
                    key={slot}
                    className="border rounded-md p-3 flex flex-wrap items-center gap-3"
                    data-testid={`intake-file-${slot}-row`}
                  >
                    <Icon className="w-4 h-4 opacity-70" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-medium">
                        {label}{required && <span className="text-destructive ml-1">*</span>}
                      </div>
                      {file ? (
                        <div className="text-[11.5px] text-muted-foreground truncate" title={file.name}>
                          {file.name} · {humanSize(file.size)}
                        </div>
                      ) : (
                        <div className="text-[11.5px] text-muted-foreground">
                          {FILE_ACCEPT_BY_KIND[kind]}
                        </div>
                      )}
                    </div>
                    <input
                      id={inputId}
                      ref={(el) => { fileInputRefs.current[slot] = el; }}
                      type="file"
                      accept={FILE_ACCEPT_BY_KIND[kind]}
                      className="sr-only"
                      onChange={(e) => setFileSlot(slot, e.target.files?.[0] || null)}
                      data-testid={`intake-file-${slot}-input`}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRefs.current[slot]?.click()}
                      data-testid={`intake-file-${slot}-btn`}
                    >
                      {file ? "Replace" : "Choose"}
                    </Button>
                    {file && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setFileSlot(slot, null)}
                        data-testid={`intake-file-${slot}-clear`}
                      >
                        <XIcon className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
            {totalBytes > 0 && (
              <p className="mt-2 text-[11.5px] text-muted-foreground" data-testid="intake-total-bytes">
                Total selected: {humanSize(totalBytes)}
              </p>
            )}
          </section>

          {/* Internal triage */}
          <section>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">
              5 — Internal triage
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setField("priority", v)}>
                  <SelectTrigger data-testid="intake-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta?.priorities || ["Low","Normal","High","Urgent"]).map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Initial workflow stage</Label>
                <Select
                  value={form.initial_workflow_status || "Demo Received"}
                  onValueChange={(v) => setField("initial_workflow_status", v)}
                >
                  <SelectTrigger data-testid="intake-initial-workflow"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(meta?.workflow_stages || ["Demo Received"]).map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Assigned reviewer (email)</Label>
                <Input
                  value={form.assigned_reviewer_email}
                  onChange={(e) => setField("assigned_reviewer_email", e.target.value)}
                  placeholder="reviewer@kandake.example"
                  data-testid="intake-reviewer"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">Intended destination</Label>
                <Select
                  value={form.intended_destination || ""}
                  onValueChange={(v) => setField("intended_destination", v === "_none" ? "" : v)}
                >
                  <SelectTrigger data-testid="intake-destination">
                    <SelectValue placeholder="(Not set)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">(Not set)</SelectItem>
                    {(meta?.intended_destinations || ["GFMP","Kingswork","Both","Undecided"]).map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[11px] uppercase tracking-wide">Internal notes (initial)</Label>
                <Textarea
                  rows={3}
                  value={form.internal_notes}
                  onChange={(e) => setField("internal_notes", e.target.value)}
                  placeholder="Anything the next reviewer needs to know at first glance."
                  data-testid="intake-internal-notes"
                />
              </div>
            </div>
          </section>

          <Separator />

          {submitError && (
            <Alert variant="destructive" data-testid="intake-error">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          )}

          <Alert>
            <AlertDescription className="text-[12.5px]">
              This record will NOT appear via the public reference-lookup endpoint.
              AI Origin Screening will run automatically on the uploaded audio.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="intake-cancel"
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting} data-testid="intake-submit">
            {submitting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            Create internal demo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default InternalDemoIntakeDialog;
